import { useEffect, useState } from 'react';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

/**
 * Detect whether the renderer is hosted in a macOS BrowserWindow. The
 * Foundry shell uses the platform-default title-bar style, so on darwin
 * the top-left ~78px of the window chrome is reserved for the
 * traffic-light controls (red/yellow/green close/minimise/zoom buttons).
 * Banners that span the full width need to inset their leading content
 * so the text doesn't render UNDERNEATH the traffic lights.
 *
 * Detection uses `navigator.platform` because the renderer process
 * doesn't have direct access to `process.platform` (contextIsolation
 * sandboxes it away). Mirrors the same pattern as `PrereqStatusList`.
 *
 * Issue #504: alpha.72 update banner had "Foundry vX.Y.Z is ready to
 * install." rendered with its leading "Foundry" word obscured by the
 * red close button on macOS. Cheap CSS-only fix — conditional left pad.
 */
function isDarwin(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /mac|darwin/i.test(navigator.platform);
}

/**
 * Top-of-window banner shown when a new version has been downloaded in the
 * background and is ready to install. Wired to the `app:update-downloaded`
 * push channel from the main process (electron-updater `update-downloaded`
 * event).
 */
interface UpdateBannerProps {
  /** When true and newUi is active, suppresses the banner so it can be
   *  rendered inside the Dashboard layout instead of at the App shell. */
  fromAppShell?: boolean;
}

export function UpdateBanner({ fromAppShell }: UpdateBannerProps = {}): React.JSX.Element | null {
  const { newUi } = useFeatureFlags();
  const [version, setVersion] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    // Live updates while this window is mounted.
    const unsubscribe = window.crucible.on('app:update-downloaded', (payload) => {
      setVersion(payload.version);
      setDismissed(false);
    });
    // Catch-up path: if the download completed before this window existed
    // (e.g. macOS dock: window was closed while alpha.N was downloading),
    // fetch the cached pending update on mount.
    window.crucible.invoke('app:get-pending-update').then((pending) => {
      if (pending) setVersion(pending.version);
    }).catch(() => { /* best-effort */ });
    return unsubscribe;
  }, []);

  if (!version || dismissed) return null;
  if (fromAppShell && newUi) return null;

  const handleInstall = () => {
    setInstalling(true);
    window.crucible.invoke('app:install-update').catch(() => setInstalling(false));
  };

  // Issue #504. On macOS, reserve ~78px of left inset for the
  // traffic-light controls so the banner copy doesn't render
  // underneath the red close button. Linux + Windows don't have the
  // inset so they keep the default `px-6`.
  const darwin = isDarwin();
  const platformPadLeft = darwin ? 'pl-20 pr-6' : 'px-6';

  if (newUi) {
    return (
      <div
        data-testid="update-banner"
        data-platform-darwin={darwin ? 'true' : 'false'}
        className={`flex flex-none items-center justify-between py-2.5 mb-4 f-body ${platformPadLeft}`}
        style={{
          background: 'var(--bg-elev-2)',
          borderBottom: '1px solid var(--border-alpha-1)',
          borderLeft: '3px solid rgba(33, 69, 247, 0.7)',
          color: 'var(--text-1)',
        }}
      >
        <span>
          <span className="font-medium">Foundry v{version}</span> is ready to install.
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={handleInstall}
            disabled={installing}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-40"
            style={{ background: 'var(--color-crucible-accent, #2145F7)' }}
          >
            {installing ? 'Installing…' : 'Install and restart'}
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="rounded-lg px-2 py-1 text-xs"
            style={{ color: 'var(--text-3)' }}
          >
            Later
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="update-banner"
      data-platform-darwin={darwin ? 'true' : 'false'}
      className={`flex flex-none items-center justify-between border-b border-crucible-accent/40 bg-crucible-accent/15 py-2.5 text-sm text-crucible-fg ${platformPadLeft}`}
    >
      <span>
        <span className="font-medium">Foundry v{version}</span> is ready to install.
      </span>
      <div className="flex items-center gap-2">
        <button
          onClick={handleInstall}
          disabled={installing}
          className="rounded-md bg-crucible-accent px-3 py-1 text-sm font-medium text-white hover:opacity-90 disabled:opacity-40"
        >
          {installing ? 'Installing…' : 'Install and restart'}
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="rounded-md px-3 py-1 text-sm text-white/60 hover:text-white/80"
        >
          Later
        </button>
      </div>
    </div>
  );
}
